import React from "react";
import QuickLookDialog from "./QuickLookDialog";

export type QuickLookEntry = {
  fileUrl: string;
  fileName?: string;
  mimeType?: string;
  isMasked?: boolean;
  uploadedAt?: string | null;
  isDuplicate?: boolean | number | string | null;
  // Source grouping — arrow-key cycling stays within the same section/source.
  sectionKey?: string;
  sourceId?: string | number | null;
};

type OpenDoc = { id: string; entry: QuickLookEntry };

type QuickLookContextValue = {
  // True only under a QuickLookProvider; consumers should hide any
  // "click to focus" affordance when this is false.
  isAvailable: boolean;
  register: (id: string, entry: QuickLookEntry) => void;
  unregister: (id: string) => void;
  focusEntry: (id: string, entry: QuickLookEntry) => void;
  clearFocusedEntry: (id: string) => void;
  // Directly open the dialog (used by the eyeball button in CustomPreviewUpload).
  openEntry: (id: string, entry: QuickLookEntry) => void;
  // Id of the thumbnail that the user has clicked to focus for Quick Look.
  focusedThumbnailId: string | null;
};

const noop = () => undefined;

const QuickLookContext = React.createContext<QuickLookContextValue>({
  isAvailable: false,
  register: noop,
  unregister: noop,
  focusEntry: noop,
  clearFocusedEntry: noop,
  openEntry: noop,
  focusedThumbnailId: null,
});

export const useQuickLook = () => React.useContext(QuickLookContext);

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  // Intentionally NOT bailing on a focused <button>: Windows/Linux browsers
  // focus a button when it is clicked (macOS does not), so after clicking a
  // thumbnail a stale button is still document.activeElement and would
  // suppress Space. We only preventDefault when a Quick Look entry is focused,
  // so normal Space-activates-button behavior is preserved when none is.
  return false;
};

// While the dialog is open, its search field holds focus (the dialog
// auto-focuses it on open), so a blanket "ignore keys while an input is
// focused" guard would permanently disable the Space/arrow shortcuts. Defer
// to the field only when it actually has text — that's when Space belongs to
// the query and arrows move the caret. When it's empty (the default state),
// the shortcuts win: Space closes, arrows cycle documents.
const isEditingText = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return target.value.length > 0;
  }
  if (target.tagName === "SELECT") return true;
  return target.isContentEditable;
};

// Stable string key for grouping documents that should cycle together.
const toSourceKey = (entry: QuickLookEntry) =>
  `${entry.sectionKey ?? ""}|${String(entry.sourceId ?? "")}`;

export const QuickLookProvider = ({ children }: { children: React.ReactNode }) => {
  // Registry of every mounted thumbnail, in insertion (render/DOM) order.
  const entriesRef = React.useRef<Map<string, QuickLookEntry>>(new Map());
  // The thumbnail that the user last clicked to focus for Quick Look.
  const focusedEntryRef = React.useRef<OpenDoc | null>(null);
  const [openDoc, setOpenDoc] = React.useState<OpenDoc | null>(null);
  const openDocRef = React.useRef<OpenDoc | null>(null);
  // Drives the visual highlight on the focused thumbnail.
  const [focusedThumbnailId, setFocusedThumbnailId] = React.useState<string | null>(null);

  React.useEffect(() => {
    openDocRef.current = openDoc;
  }, [openDoc]);

  const register = React.useCallback((id: string, entry: QuickLookEntry) => {
    entriesRef.current.set(id, entry);
  }, []);

  const unregister = React.useCallback((id: string) => {
    entriesRef.current.delete(id);
    if (focusedEntryRef.current?.id === id) {
      focusedEntryRef.current = null;
      setFocusedThumbnailId(null);
    }
    // If the document open in Quick Look was removed (deleted, moved out of
    // view, list refetched), close the dialog rather than leaving it showing
    // a stale entry with a dead URL and a cycle that no longer includes it.
    if (openDocRef.current?.id === id) {
      setOpenDoc(null);
    }
  }, []);

  const focusEntry = React.useCallback((id: string, entry: QuickLookEntry) => {
    focusedEntryRef.current = { id, entry };
    setFocusedThumbnailId(id);
  }, []);

  const clearFocusedEntry = React.useCallback((id: string) => {
    if (focusedEntryRef.current?.id === id) {
      focusedEntryRef.current = null;
      setFocusedThumbnailId(null);
    }
  }, []);

  const openEntry = React.useCallback((id: string, entry: QuickLookEntry) => {
    setOpenDoc({ id, entry });
  }, []);

  // All registered documents that share the same section/source as the given id,
  // in DOM insertion order. Used for left/right cycling in the dialog.
  const getSourceCycle = React.useCallback((id: string): OpenDoc[] => {
    const currentEntry = entriesRef.current.get(id);
    if (!currentEntry) return [];
    const targetKey = toSourceKey(currentEntry);
    const result: OpenDoc[] = [];
    entriesRef.current.forEach((entry, entryId) => {
      if (toSourceKey(entry) === targetKey) result.push({ id: entryId, entry });
    });
    return result;
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const isSpace = event.key === " " || event.code === "Space";
      const isEsc = event.key === "Escape";
      const isArrow = event.key === "ArrowLeft" || event.key === "ArrowRight";

      // Dialog open: space/esc close, arrows cycle through the source. Esc
      // always closes, even while typing in the dialog's own search field;
      // space/arrows must not hijack in-progress text editing (typing a
      // multi-word search, moving the caret) — but do fire when the
      // (auto-focused) search field is empty.
      if (openDocRef.current) {
        if (isEsc) {
          event.preventDefault();
          setOpenDoc(null);
          return;
        }
        if (isEditingText(event.target)) return;
        if (isSpace) {
          event.preventDefault();
          setOpenDoc(null);
          return;
        }
        if (isArrow) {
          const cycle = getSourceCycle(openDocRef.current.id);
          if (cycle.length < 2) return;
          event.preventDefault();
          const currentIndex = cycle.findIndex(
            (item) => item.id === openDocRef.current?.id
          );
          const delta = event.key === "ArrowRight" ? 1 : -1;
          const nextIndex = (currentIndex + delta + cycle.length) % cycle.length;
          setOpenDoc(cycle[nextIndex]);
        }
        return;
      }

      // Dialog closed: space opens the focused thumbnail (if any).
      if (!isSpace) return;
      if (isTypingTarget(event.target)) return;

      if (focusedEntryRef.current) {
        event.preventDefault();
        setOpenDoc(focusedEntryRef.current);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [getSourceCycle]);

  const value = React.useMemo(
    () => ({
      isAvailable: true,
      register,
      unregister,
      focusEntry,
      clearFocusedEntry,
      openEntry,
      focusedThumbnailId,
    }),
    [register, unregister, focusEntry, clearFocusedEntry, openEntry, focusedThumbnailId]
  );

  const sourceCycle = openDoc ? getSourceCycle(openDoc.id) : [];
  const sourceTotal = sourceCycle.length;
  const sourcePosition = openDoc
    ? sourceCycle.findIndex((item) => item.id === openDoc.id) + 1
    : 0;

  return (
    <QuickLookContext.Provider value={value}>
      {children}
      <QuickLookDialog
        openDoc={openDoc}
        onClose={() => setOpenDoc(null)}
        sourcePosition={sourcePosition}
        sourceTotal={sourceTotal}
      />
    </QuickLookContext.Provider>
  );
};
